import { useCameraName } from '../../../shared/hooks/useCameraName'
import { useClipLoaderState } from '../../../shared/hooks/useClipLoaderState'
import { useClipLoaderTracking } from '../../../shared/hooks/useClipLoaderTracking'
import { useLastClipPreview } from '../../../shared/hooks/useLastClipPreview'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'

import { FacilityType } from '@/graphql/generated/schemas'
import { isManualIncident } from '@/utils/incidents'

import {
  IncidentDataType,
  MixpanelDataIProps,
} from '../../../shared/types/types'
import { ClipLoader } from './ClipLoader'
import { IncidentVideo } from './IncidentVideo'
import { MarketIncidentMap } from './MarketIncidentMap'
import { IncidentImages } from './incident-images/IncidentImages'

export interface MediaPlayerIProps {
  incident: IncidentDataType
  isLoading: boolean
  mixpanelData: MixpanelDataIProps
  scrollToTop: () => void
  /** Render the view-toolbar player (used by both the legacy and redesigned drawers). */
  useViewToolbar?: boolean
}

export const MediaPlayer = ({
  incident,
  isLoading,
  mixpanelData,
  scrollToTop,
  useViewToolbar = false,
}: MediaPlayerIProps) => {
  const {
    shouldShowClipLoader,
    shouldShowIncidentVideo,
    clipLabel,
    shouldDefaultToLive,
    hasTimedOut,
    hasClickedLive,
    handleClipClick,
    handleLiveClick,
  } = useClipLoaderState({
    incidentId: incident?.id,
    incidentType: incident?.type,
    hasVideoClip: incident?.hasVideoClip,
    createdAt: incident?.createdAt,
    isLoading,
  })

  const { onClipLoaderShown, trackViewedLive, trackClipClicked } =
    useClipLoaderTracking({
      incidentId: incident?.id,
      mixpanelData,
      hasTimedOut,
      hasVideoClip: incident?.hasVideoClip,
      hasClickedLive,
    })

  const device = incident?.devices?.[0]

  const isClipLoaderPreviewEnabled =
    useFeatureFlag(FeatureFlag.clipLoaderPreview) ||
    useFeatureFlag(FeatureFlag.development)

  const {
    previewImageUrl,
    HiddenPlayer,
    previewClipAgeSeconds,
    previewLoadDurationMs,
  } = useLastClipPreview({
    deviceId: device?.id,
    enabled: shouldShowClipLoader && isClipLoaderPreviewEnabled,
  })

  const cameraName = useCameraName({
    deviceId: device?.id,
    deviceName: device?.name,
    deviceType: device?.type,
  })

  if (!incident) {
    return null
  }

  const handleLiveClickWithAnalytics = () => {
    trackViewedLive()
    handleLiveClick()
  }

  const handleClipClickWithAnalytics = () => {
    trackClipClicked()
    handleClipClick()
  }

  return (
    <>
      {!isManualIncident(incident) && shouldShowClipLoader && (
        <>
          <HiddenPlayer />
          <ClipLoader
            deviceName={cameraName}
            hasTimedOut={hasTimedOut}
            hasVideoClip={incident?.hasVideoClip}
            incidentId={incident?.id}
            mixpanelData={mixpanelData}
            onClipClick={handleClipClickWithAnalytics}
            onLiveClick={handleLiveClickWithAnalytics}
            onShown={onClipLoaderShown}
            previewClipAgeSeconds={previewClipAgeSeconds}
            previewImageUrl={previewImageUrl}
            previewLoadDurationMs={previewLoadDurationMs}
            useViewToolbar={useViewToolbar}
          />
        </>
      )}
      {!isManualIncident(incident) && shouldShowIncidentVideo && (
        <IncidentVideo
          clipLabel={clipLabel}
          createdAt={incident?.createdAt}
          defaultToLive={shouldDefaultToLive}
          deviceId={device?.id}
          hasTimedOut={hasTimedOut}
          hasVideoClip={incident?.hasVideoClip}
          incidentId={incident?.id}
          incidentType={incident?.type}
          mixpanelData={mixpanelData}
          onClipClick={handleClipClickWithAnalytics}
          scrollToTop={scrollToTop}
          useViewToolbar={useViewToolbar}
        />
      )}
      {incident?.facility?.type === FacilityType.Market && (
        <MarketIncidentMap incident={incident} />
      )}
      {incident?.imageUrls?.length > 0 && (
        <IncidentImages images={incident?.imageUrls} />
      )}
    </>
  )
}
